// Permission vocabulary — single source of truth for the intranet's
// team-based access control. 30 keys, grouped by domain. The server
// mirrors this file at `server/src/core/constants/permissions.js`
// and must stay in sync (see NEEDED_FOR_SERVER.md §I11).
//
// Effective permissions for a user in an org =
//   owner-bypass  ∪  OrgMember.role perms  ∪  team perms  ∪  ancestor team perms
//
// Checked in:
//   • Governance RLS (`jwt_has_perm(key)` — migration 0060)
//   • Server endpoints that mutate teams / perms
//   • Intranet UI gates (admin matrix, tab visibility)
//
// No wildcards by design — enumerate effective perms exactly.

// ── Permission keys ────────────────────────────────────────────────

export const PERMISSIONS = {
  // Admin (settings-page surfaces; not "superuser")
  'admin.pages.read':        { group: 'Admin',     label: 'Access /admin surfaces',  description: 'Read-level access to any /admin page (blanket gate; specific write perms layered on top)' },
  'admin.users':             { group: 'Admin',     label: 'Manage users',            description: 'Invite, deactivate, reset password, view all accounts' },
  'admin.teams':             { group: 'Admin',     label: 'Manage teams',            description: 'Create, rename, delete teams; add/remove members' },
  'admin.team_permissions':  { group: 'Admin',     label: 'Edit team permissions',   description: 'Change the permission matrix on any team (separate from membership to prevent self-escalation)' },
  'admin.billing':           { group: 'Admin',     label: 'Manage billing',          description: 'Subscription tier, payment methods, plan changes' },
  'admin.integrations':      { group: 'Admin',     label: 'Manage integrations',     description: 'Connect and configure external services' },
  'admin.commercials':       { group: 'Admin',     label: 'Manage commercials',      description: 'Edit public-facing services, packages, pricing' },
  'admin.cap_table':         { group: 'Admin',     label: 'Manage cap table',        description: 'View + edit cap table, grants, vesting' },
  'admin.hr':                { group: 'Admin',     label: 'Edit HR fields',          description: 'Title, start date, position on other users\' profiles' },
  'admin.logs':              { group: 'Admin',     label: 'View activity logs',      description: 'Audit trail + security logs' },
  'admin.system':            { group: 'Admin',     label: 'System administration',   description: 'System status, danger zone, data export' },
  'admin.company':           { group: 'Admin',     label: 'Edit company page',       description: 'Edit /company content' },

  // Docs
  'docs.edit_others':        { group: 'Docs',      label: 'Edit others\' docs',      description: 'Modify docs authored by other people' },
  'docs.delete_others':      { group: 'Docs',      label: 'Delete others\' docs',    description: 'Remove docs authored by other people' },
  'docs.read_private':       { group: 'Docs',      label: 'Read private docs',       description: 'View docs not explicitly shared with you' },

  // Knowledge base
  'kb.edit_others':          { group: 'KB',        label: 'Edit others\' KB pages',  description: 'Modify wiki pages authored by other people' },
  'kb.delete_others':        { group: 'KB',        label: 'Delete others\' KB pages', description: 'Remove wiki pages authored by other people' },

  // Tickets
  'tickets.assign':          { group: 'Tickets',   label: 'Assign tickets',          description: 'Set assignee on tickets' },
  'tickets.close_others':    { group: 'Tickets',   label: 'Close others\' tickets',  description: 'Mark as done tickets you don\'t own' },
  'tickets.delete_others':   { group: 'Tickets',   label: 'Delete others\' tickets', description: 'Remove tickets you don\'t own' },
  'tickets.manage_columns':  { group: 'Tickets',   label: 'Manage ticket workflow',  description: 'Edit board columns, statuses, order' },

  // Stories / announcements
  'stories.post':            { group: 'Stories',   label: 'Post announcements',      description: 'Publish org-wide stories and announcements' },
  'stories.edit_others':     { group: 'Stories',   label: 'Edit others\' stories',   description: 'Modify stories authored by other people' },
  'stories.delete_others':   { group: 'Stories',   label: 'Delete others\' stories', description: 'Remove stories authored by other people' },
  'stories.pin':             { group: 'Stories',   label: 'Pin stories',             description: 'Promote stories to the top of the feed' },

  // Calendar
  'calendar.create_org_events': { group: 'Calendar', label: 'Create org events',     description: 'Create events visible to the whole org' },
  'calendar.edit_others':    { group: 'Calendar',  label: 'Edit others\' events',    description: 'Modify events organized by other people' },

  // Chat
  'chat.create_channels':    { group: 'Chat',      label: 'Create channels',         description: 'Add new chat channels' },
  'chat.archive_channels':   { group: 'Chat',      label: 'Archive channels',        description: 'Archive chat channels' },
  'chat.delete_others':      { group: 'Chat',      label: 'Delete others\' messages', description: 'Remove chat messages from other people' },

  // Grants
  'grants.view_others':      { group: 'Grants',    label: 'View others\' grants',    description: 'See cap-table detail for grants belonging to other people' }
}

export const PERMISSION_KEYS = Object.keys(PERMISSIONS)
export const PERMISSION_GROUPS = [...new Set(PERMISSION_KEYS.map((k) => PERMISSIONS[k].group))]

// ── OrgMember.role → implicit perm sets ────────────────────────────
// Union with team perms. `owner` bypasses at the claim-synthesis
// layer — exposed here only for reference / explicit checks.

export const ORG_ROLE_PERMISSIONS = {
  owner: PERMISSION_KEYS,                                      // bypass; every key
  admin: PERMISSION_KEYS.filter((k) => k.startsWith('admin.')), // all admin.*
  member: []
}

// ── System team seed defaults ──────────────────────────────────────
// Used by `seed-system-teams.mjs` on the server. Admins can edit
// each team's perms after seed. Slugs are the stable identifier;
// display names can be changed per-org.

export const SYSTEM_TEAMS = [
  // Active category — members of these teams see the full intranet.
  { slug: 'members',  name: 'Members',  category: 'active', permissions: [] },
  { slug: 'admin',    name: 'Admin',    category: 'active', permissions: PERMISSION_KEYS.filter((k) => k.startsWith('admin.')) },
  { slug: 'manager',  name: 'Manager',  category: 'active', permissions: [
    'tickets.assign', 'tickets.close_others',
    'calendar.create_org_events',
    'stories.post',
    'docs.edit_others'
  ] },
  { slug: 'hr',       name: 'HR',       category: 'active', permissions: ['admin.hr', 'admin.users'] },
  { slug: 'finance',  name: 'Finance',  category: 'active', permissions: ['admin.billing', 'admin.cap_table', 'grants.view_others'] },
  { slug: 'content',  name: 'Content',  category: 'active', permissions: ['docs.edit_others', 'kb.edit_others', 'stories.post', 'stories.pin'] },

  // External category — trimmed intranet surface.
  { slug: 'advisors',  name: 'Advisors',  category: 'external', permissions: [] },
  { slug: 'investors', name: 'Investors', category: 'external', permissions: [] },
  { slug: 'alumni',    name: 'Alumni',    category: 'external', permissions: [] }
]

export const SYSTEM_TEAM_SLUGS = new Set(SYSTEM_TEAMS.map((t) => t.slug))

// Team category enum. 'active' members see full intranet; 'external'
// see trimmed UI; 'archived' hidden.
export const CATEGORIES = ['active', 'external', 'archived']

// Orgs where `public.people` is the legacy roster — `user_profiles`
// rows predate multi-tenancy and were never workspace-scoped, so these
// orgs trust the view directly instead of intersecting with Mongo
// OrgMember. Drop an id here once its user_profiles have been fully
// provisioned as Mongo Users + OrgMembers.
export const LEGACY_ORG_IDS = new Set([
  '69b77a61e0e0de1aa47d01cb' // Symbols System — original pre-multitenancy home
])

// Precomputed at module load so render factories don't re-filter the
// 30-key list per group per tick.
export const PERMISSIONS_BY_GROUP = Object.fromEntries(
  PERMISSION_GROUPS.map((g) => [g, PERMISSION_KEYS.filter((k) => PERMISSIONS[k].group === g)])
)

// ── Runtime helpers — consume JWT claims ───────────────────────────
// Claims shape is set by symbols-auth-bridge: `is_owner`, `org_role`,
// `teams[{id,slug,category}]`, `permissions[]`.

export const hasPerm = (claims, key) =>
  !!claims && (claims.is_owner === true || (claims.permissions || []).includes(key))

export const isActiveMember = (claims) =>
  !!claims && (claims.teams || []).some((t) => t.category === 'active')

export const isInTeam = (claims, slug) =>
  !!claims && (claims.teams || []).some((t) => t.slug === slug)

export const isOwner = (claims) => !!claims && claims.is_owner === true

export const isAdmin = (claims) =>
  !!claims && (claims.org_role === 'owner' || claims.org_role === 'admin')

// Is the user allowed to see any gov UI surface beyond the external
// trim? Owners/admins bypass the team check; otherwise must belong to
// an active-category team.
export const canAccessGovUI = (claims) =>
  isOwner(claims) || isAdmin(claims) || isActiveMember(claims)

// Effective category from a list of team entries: active beats
// external beats none. Used by the /people category sidebar.
export const effectiveCategory = (teams) => {
  if (!teams) return 'none'
  if (teams.some((t) => t.category === 'active')) return 'active'
  if (teams.some((t) => t.category === 'external')) return 'external'
  return 'none'
}

// Redirect-gate for pages gated on active-member access (Chat,
// Tickets, Documents). Returns true if a redirect was issued.
export const redirectIfExternalOnly = function (s) {
  const el = this
  const claims = s?.root?.jwtClaims
  // Claims still loading — don't bounce.
  if (!claims || !Array.isArray(claims.teams)) return false
  if (canAccessGovUI(claims)) return false
  const root = el.call('findRouteRoot', el)
  if (root) el.router('/', root, {}, { pushState: true })
  return true
}

export const permLabel = (key) => PERMISSIONS[key]?.label || key
export const permDescription = (key) => PERMISSIONS[key]?.description || ''
export const permGroup = (key) => PERMISSIONS[key]?.group || 'Other'
